import React, { useState, useEffect } from 'react';
import { createQuestion, updateQuestion } from '../../api/faq';
import adminApi from '../../api/admin';
import { useAuth } from '../../context/AuthContext';

const FAQQuestionDialog = ({ open, onClose, onQuestionCreated, onQuestionUpdated, question = null }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    question: '',
    is_pinned: false,
    role_ids: []
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditing = question !== null;
  const isAdmin = user && (user.is_staff || user.is_superuser);

  useEffect(() => {
    if (open) {
      loadRoles();
      if (isEditing) {
        setFormData({
          title: question.title,
          question: question.question,
          is_pinned: question.is_pinned,
          role_ids: question.roles.map(role => role.id)
        });
      } else {
        setFormData({
          title: '',
          question: '',
          is_pinned: false,
          role_ids: []
        });
      }
      setError('');
    }
  }, [open, question, isEditing]);

  const loadRoles = async () => {
    try {
      const data = await adminApi.getRoles();
      setRoles(data);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isEditing) {
        await updateQuestion(question.id, formData);
        onQuestionUpdated();
      } else {
        await createQuestion(formData);
        onQuestionCreated();
      }
    } catch (err) {
      console.error('Error saving question:', err);
      setError(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRoleChange = (roleId) => {
    setFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId]
    }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div 
        className="absolute inset-0 bg-black opacity-50"
        onClick={onClose}
      ></div>
      <div className="bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-lg z-10 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
              {isEditing ? 'Edit Question' : 'Ask a Question'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter a clear, descriptive title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Question
              </label>
              <textarea
                name="question"
                value={formData.question}
                onChange={handleInputChange}
                required
                rows="8"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                placeholder="Describe your question in detail. You can use Markdown formatting."
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Supports Markdown formatting
              </p>
            </div>

            {isAdmin && (
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="is_pinned"
                    checked={formData.is_pinned}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pin this question
                  </span>
                </label>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Visible to Roles
              </label>
              <div className="space-y-2 max-h-32 overflow-y-auto border border-ui-light-border dark:border-ui-dark-border rounded-md p-3">
                {/* Only show "Public (all users)" if there's no Public role in the database */}
                {!roles.find(role => role.name.toLowerCase() === 'public') && (
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.role_ids.length === 0}
                      onChange={() => setFormData(prev => ({ ...prev, role_ids: [] }))}
                      className="rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500 dark:bg-ui-dark-background"
                    />
                    <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      Public (all users)
                    </span>
                  </label>
                )}
                {roles.map(role => (
                  <label key={role.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.role_ids.includes(role.id)}
                      onChange={() => handleRoleChange(role.id)}
                      className="rounded border-ui-light-border dark:border-ui-dark-border text-primary-600 focus:ring-primary-500 dark:bg-ui-dark-background"
                    />
                    <span className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                      {role.name}
                    </span>
                  </label>
                ))}
              </div>
              <p className="mt-1 text-sm text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">
                Select roles that can see this question. {!roles.find(role => role.name.toLowerCase() === 'public') ? 'Leave none selected for public visibility.' : 'Select the Public role for public visibility.'}
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : (isEditing ? 'Update Question' : 'Post Question')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FAQQuestionDialog;